import KendoTable, {
  type ColumnConfig,
} from "@/app/components/common/table/KendoTable";
import {
  Checkbox,
  type CheckboxChangeEvent,
} from "@progress/kendo-react-inputs";
import { type GridRowClickEvent } from "@progress/kendo-react-grid";
import {
  type Beneficiary,
  type GlobalCountry,
} from "@/app/types/beneficiaries";
import "../Dashboard.scss";
import {
  BeneApproverCell,
  DateCellOne,
} from "@/app/components/common/table/KendoCustomCells";
import { useMemo } from "react";
import type { BeneficiaryAccountCellProps } from "../../beneficiaries/Beneficiaryconstants";
import MaskedAccountNumber from "@/app/utils/helpers/MaskedAccountNumber";
import { t } from "i18next";

interface BeneficiaryApprovalTableProps {
  onRowClick: (event: GridRowClickEvent) => void;
  showAll?: boolean;
  beneficiaries: Beneficiary[];
  selectedBeneficiaries?: number[];
  setSelectedBeneficiaries?: React.Dispatch<React.SetStateAction<number[]>>;
  setCheckAll?: React.Dispatch<React.SetStateAction<boolean>>;
  checkAll?: boolean;
  approveAccess: boolean;
  countryList: GlobalCountry[];
}

interface BeneficiaryNameCellProps {
  dataItem: {
    beneNickname?: string;
    [key: string]: unknown;
  };
}

interface BeneficiaryCountryCellProps {
  dataItem: {
    bankCountryName?: string;
    beneCountryCode2?: string;
    beneBankCountryCode2?: string; // Newly added key
    [key: string]: unknown;
  };
  countryList?: GlobalCountry[];
}

// Options interface to keep buildColumns params under the 7-param limit
interface BuildColumnsOptions {
  approveAccess: boolean;
  checkAll: boolean;
  beneficiaries: Beneficiary[];
  selectedBeneficiaries: number[];
  countryList: GlobalCountry[];
  setCheckAll?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedBeneficiaries?: React.Dispatch<React.SetStateAction<number[]>>;
}

export const BeneficiaryCountryCell: React.FC<BeneficiaryCountryCellProps> = ({
  dataItem,
  countryList = [],
}) => {
  const countryCode =
    dataItem?.beneBankCountryCode2 ??
    dataItem?.beneCountryCode2 ??
    dataItem?.bankCountryName;

  const countryName = useMemo(() => {
    if (!countryCode) return "--";

    const match = countryList.find(
      (item: GlobalCountry) => item.countryCode2 === countryCode,
    );
    return match ? match.countryName : countryCode;
  }, [countryCode, countryList]);

  return (
    <div>
      <span
        className="gx-cell-date-text gx-text-ellipsis gx-p10-m"
        title={countryName}
      >
        {countryName}
      </span>
    </div>
  );
};

const BeneficiaryNameCell: React.FC<BeneficiaryNameCellProps> = ({
  dataItem,
}) => {
  const fullName = (dataItem?.beneName as string) ?? "--";

  const initials = (() => {
    if (!fullName) return "--";

    const parts = fullName.trim().split(" ").filter(Boolean);

    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }

    return (
      parts[0].charAt(0).toUpperCase() + parts.at(-1)!.charAt(0).toUpperCase()
    );
  })();

  return (
    <div className="gx-cell-name-wrapper">
      <div className="gx-cell-name-content">
        <div className="gx-cell-name-avatar">
          <span className="gx-p12-b">{initials}</span>
        </div>
        <div className="gx-cell-name-info">
          <div className="gx-text-ellipsis_name" title={fullName}>
            <span className="gx-p12-b gx-text-ellipsis_name">{fullName}</span>
          </div>
          {dataItem?.beneNickname && (
            <p
              className="gx-text-ellipsis_name gx-text_ac-Number gx-p10-m"
              title={dataItem?.beneNickname}
            >{`Nick Name : ${dataItem?.beneNickname}`}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export const BeneficiaryAccountCell: React.FC<BeneficiaryAccountCellProps> = ({
  dataItem,
}) => {
  return (
    <div className="gx-text-ellipsis" title={dataItem?.maskBeneAccountNo}>
      <div className="gx-text_ac_number">
        <span className="scrollable-text">
          <MaskedAccountNumber
            accountNumber={dataItem?.beneAccountNo ?? ""}
            maskedAccountNumber={dataItem?.maskBeneAccountNo ?? ""}
            showToggle
            toggleIconClassName="w-3 h-3 cursor-pointer opacity-70 hover:opacity-100"
            showCopy
          />
        </span>
      </div>
      {dataItem?.beneBankName && (
        <p className="gx-text-ellipsis gx-p10-m" title={dataItem?.beneBankName}>
          {`${t("bank")} : ${dataItem?.beneBankName ?? "--"}`}
        </p>
      )}
    </div>
  );
};

const buildColumns = (
  showAll: boolean,
  options: BuildColumnsOptions,
): ColumnConfig[] => {
  const {
    approveAccess,
    checkAll,
    beneficiaries,
    selectedBeneficiaries,
    countryList,
    setCheckAll,
    setSelectedBeneficiaries,
  } = options;

  if (!showAll) {
    return [
      {
        field: "beneName",
        title: "Name",
        width: 120,
        cell: (props) => (
          <td className="gx-name-cell-with-pin">
            <BeneficiaryNameCell {...props} />
          </td>
        ),
      },
      {
        field: "beneNameFull",
        title: "Full Name",
        width: 150,
        cell: (props) => <td>{props.dataItem?.beneName ?? "--"}</td>,
      },
      {
        field: "maskBeneAccountNo",
        title: t("account_number_iban"),
        width: 300,
        cell: BeneficiaryAccountCell,
      },
      {
        field: "beneCountryCode2",
        title: "Country",
        width: 200,
        cell: (props) => (
          <td>
            <BeneficiaryCountryCell countryList={countryList} {...props} />
          </td>
        ),
      },
      {
        field: "crtdDt",
        title: "Date",
        width: 200,
        cell: (props) => <DateCellOne {...props} />,
      },
      {
        field: "noOfApprLevel",
        title: "Approval Level",
        width: 150,
        cell: (props) => <BeneApproverCell {...props} />,
      },
    ];
  }

  return [
    {
      field: "",
      headerCell: approveAccess ? (
        <Checkbox
          className="gx-font-medium"
          checked={checkAll}
          onChange={(event: CheckboxChangeEvent) => {
            if (!setCheckAll) return;

            setCheckAll(event?.value);

            if (!setSelectedBeneficiaries) return;

            if (event?.value === false) {
              setSelectedBeneficiaries([]);
            } else {
              const selected = beneficiaries
                .slice(0, 10)
                .map((item: Beneficiary) => item?.beneId);
              setSelectedBeneficiaries(selected);
            }
          }}
        />
      ) : null,
      cell: (prop) => (
        <td className="check-box-cell" onClick={(e) => e.stopPropagation()}>
          {approveAccess && (
            <Checkbox
              checked={selectedBeneficiaries?.includes(prop?.dataItem?.beneId)}
              onClick={(e) => e.stopPropagation()}
              onChange={() => {
                const id = prop.dataItem?.beneId;
                const isSelected = selectedBeneficiaries.includes(id);

                if (!setSelectedBeneficiaries) return;

                if (isSelected) {
                  setSelectedBeneficiaries(
                    selectedBeneficiaries.filter((x) => x !== id),
                  );
                } else {
                  setSelectedBeneficiaries((prev) => [...prev, id]);
                }

                if (setCheckAll) setCheckAll(false);
              }}
            />
          )}
        </td>
      ),
      width: 45,
    },
    {
      field: "beneNickname",
      title: "Name",
      width: 200,
      cell: (props) => (
        <td>
          <BeneficiaryNameCell {...props} />
        </td>
      ),
    },
    {
      field: "maskBeneAccountNo",
      title: t("account_number_iban"),
      width: 300,
      cell: BeneficiaryAccountCell,
    },
    {
      field: "beneCountryCode2",
      title: "Country",
      width: 200,
      cell: (props) => (
        <td>
          <BeneficiaryCountryCell countryList={countryList} {...props} />
        </td>
      ),
    },
    {
      field: "swiftBicCode",
      title: "Swift BicCode",
      width: 200,
      cell: (props) => (
        <td
          className="gx-p10-m gx-text-ellipsis"
          title={props.dataItem?.beneBankSwiftBicCode ?? "--"}
        >
          {props.dataItem?.beneBankSwiftBicCode ?? "--"}
        </td>
      ),
    },
    {
      field: "crtdDt",
      title: "Date",
      width: 180,
      cell: (props) => <DateCellOne {...props} />,
    },
    {
      field: "noOfApprLevel",
      title: "Approval Level",
      width: 150,
      cell: (props) => <BeneApproverCell {...props} />,
    },
  ];
};

const BeneficiaryApprovalTable: React.FC<BeneficiaryApprovalTableProps> = ({
  onRowClick,
  showAll = false,
  beneficiaries,
  selectedBeneficiaries = [],
  setSelectedBeneficiaries,
  setCheckAll,
  checkAll = false,
  approveAccess = false,
  countryList,
}) => {
  const columns = buildColumns(showAll, {
    approveAccess,
    checkAll,
    beneficiaries,
    selectedBeneficiaries,
    countryList,
    setCheckAll,
    setSelectedBeneficiaries,
  });

  return (
    <KendoTable
      data={beneficiaries}
      columns={columns}
      onRowClick={onRowClick}
      className="kendo-grid-beneficiaries"
    />
  );
};

export default BeneficiaryApprovalTable;
